/* eslint-disable react/require-default-props */
import React, { useRef, useState } from 'react';
import { Popup } from '@progress/kendo-react-popup';
import { Button } from '@ui';
import { translate } from '@utils/i18n';
import ClickAwayListener from 'react-click-away-listener';
import TooltipText from '@components/v2/TooltipText';
import './ComponentTypeSelectorStyles.scss';
const namespace = 'features.settings.component_types';
function ComponentTypeSelector({ selectedTypeIds, availableTypes, currentTypeId, onChange, onBlur, lockedTypeIds = [] }) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const anchor = useRef(null);
    // Filter out current type and already selected types
    const filteredTypes = availableTypes.filter((type) => type.id !== currentTypeId && !selectedTypeIds.includes(type.id));
    const handleAddType = (typeId) => {
        setDropdownOpen(false);
        onChange([...selectedTypeIds, typeId]);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur();
    };
    const handleRemoveType = (typeId) => {
        if (lockedTypeIds.includes(typeId))
            return;
        onChange(selectedTypeIds.filter((id) => id !== typeId));
        onBlur === null || onBlur === void 0 ? void 0 : onBlur();
    };
    const selectedTypes = availableTypes.filter((type) => selectedTypeIds.includes(type.id));
    return (React.createElement("div", { className: "component-type-selector" },
        React.createElement("div", { className: "component-type-selector__content" },
            selectedTypes.map((type) => (React.createElement(TypeTag, { key: type.id, type: type, onRemove: () => handleRemoveType(type.id), removeAllowed: !lockedTypeIds.includes(type.id) }))),
            React.createElement(Button, { ref: anchor, variant: "secondary", "aria-label": translate('add_compatible_type', { namespace }), onClick: (e) => {
                    e.stopPropagation();
                    setDropdownOpen(!dropdownOpen);
                } },
                React.createElement("i", { className: "fa-solid fa-plus" })),
            React.createElement(Popup, { anchor: anchor.current, show: dropdownOpen },
                React.createElement(TypeDropdown, { types: filteredTypes, onAddType: handleAddType, onClosePopup: () => setDropdownOpen(false) })))));
}
function TypeDropdown({ onAddType, onClosePopup, types }) {
    const searchRef = useRef(null);
    const [filteredTypes, setFilteredTypes] = useState(types);
    React.useEffect(() => {
        var _a;
        (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        setFilteredTypes(types);
    }, [types]);
    const onHandleKeyUp = (event) => {
        const searchValue = event.target.value.toLowerCase();
        setFilteredTypes(types.filter((type) => type.typeName.toLowerCase().includes(searchValue)));
    };
    return (React.createElement(ClickAwayListener, { onClickAway: onClosePopup },
        React.createElement("div", { className: "component-type-dropdown" },
            React.createElement("div", { className: "qmb-input--blank" },
                React.createElement("input", { ref: searchRef, onKeyUp: onHandleKeyUp, placeholder: translate('search_types', { namespace }) })),
            React.createElement("div", { className: "component-type-dropdown__list" },
                filteredTypes.length === 0 && (React.createElement("div", { className: "qmb-control qmb-control--disabled" }, translate('no_types_found', { namespace }))),
                filteredTypes.length > 0 &&
                    filteredTypes.map((type) => React.createElement(DropdownItem, { key: type.id, type: type, onHandleClick: onAddType }))))));
}
function DropdownItem({ type, onHandleClick }) {
    return (React.createElement("button", { className: "qmb-control", type: "button", onClick: () => onHandleClick(type.id) },
        React.createElement("div", { className: "component-type-item" },
            React.createElement("span", { className: "component-type-item__name" }, type.typeName),
            type.category && React.createElement("span", { className: "component-type-item__category" }, type.category))));
}
function TypeTag({ type, onRemove, removeAllowed = true }) {
    const removeButton = (React.createElement("button", { className: "qmb-control--icon fa-light fa-xmark", type: "button", "aria-label": `Remove ${type.typeName}`, onClick: onRemove, disabled: !removeAllowed }));
    return (React.createElement("span", { className: "qmb-tag--gray" },
        type.typeName,
        !removeAllowed ? (React.createElement(TooltipText, { text: translate('tooltips.cannot_remove_compatible_type', { namespace }) }, removeButton)) : (removeButton)));
}
ComponentTypeSelector.defaultProps = {
    onBlur: undefined,
    lockedTypeIds: []
};
export default ComponentTypeSelector;
